import { useTranslation } from 'react-i18next';
import moment from 'moment';
import writeXlsxFile, { SheetData, Cell } from 'write-excel-file';

import { DownloadIcon } from '@treams/treams-icons';
import { Button, ITimeMachine } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';

import { tableCellValue } from './AnalyticsMetricDetails';

export default function AnalyticsMetricDetailsExport(props: {
	detail: graphql.AnalyticsDetail;
	rows: Record<string, graphql.AnalyticsDetailColumnValue | null | undefined>[];
	filters: graphql.AnalyticsFiltersV2;
	timeMachine: ITimeMachine;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const hasFraction = props.detail.headers.some((header) => header.type === 'fraction');

	const columnWidths: Record<string, number | number[]> = {
		fraction: [8, 8, 8],
		user: 20,
		department: 20,
		departments: 20,
		managers: 20,
		title: 30,
		feedbackTitle: 30,
		createdAt: 20,
		feedbackGivenCount: 12,
		feedbackAnsweredCount: 12,
		feedbackRequestCount: 12,
		momentCount: 12,
		momentCompletedCount: 12,
	};

	const columnWrap: Record<string, boolean> = {
		managers: true,
		departments: true,
		department: true,
	};

	const headers: SheetData = [
		props.detail.headers.flatMap((header) => {
			const headerValue = t(`analytics.metric.details.${header.key}`);

			if (header.type === 'fraction') {
				return [
					{
						fontWeight: 'bold',
						backgroundColor: '#eeeeee',
						bottomBorderStyle: !hasFraction ? 'thick' : undefined,
						value: headerValue,
						span: 3,
					},
					null,
					null,
				];
			}

			return [
				{
					value: t(`analytics.metric.details.${header.key}`),
					fontWeight: 'bold',
					backgroundColor: '#eeeeee',
					bottomBorderStyle: 'thick',
					rowSpan: hasFraction ? 2 : 1,
					span: 1,
					wrap: true,
				},
			];
		}),
		...(hasFraction
			? [
					props.detail.headers.flatMap((header) => {
						if (header.type === 'fraction') {
							return [
								{
									value: t('analytics.export.columns.fraction.numerator'),
									fontWeight: 'bold',
									backgroundColor: '#eeeeee',
									bottomBorderStyle: 'thick',
								},
								{
									value: t('analytics.export.columns.fraction.denominator'),
									fontWeight: 'bold',
									backgroundColor: '#eeeeee',
									bottomBorderStyle: 'thick',
								},
								{
									value: t('analytics.export.columns.fraction.percentage'),
									fontWeight: 'bold',
									backgroundColor: '#eeeeee',
									bottomBorderStyle: 'thick',
								},
							] as Cell;
						}

						return [null];
					}),
			  ]
			: []),
	];

	const rows: SheetData = props.rows.map((row) => {
		return props.detail.headers.flatMap((header) => {
			const value = tableCellValue(localize, row, header);

			if (header.type === 'fraction') {
				if (
					typeof value.excel?.value !== 'object' ||
					!('value' in value.excel.value) ||
					!('total' in value.excel.value)
				) {
					return [null, null, null];
				}

				const total = value.excel.value.total;

				return [
					{
						type: Number,
						value: value.excel.value.value,
					},
					{
						type: Number,
						value: total,
					},
					{
						type: Number,
						value: total > 0 ? value.excel.value.value / total : 0,
						format: '0.0%',
					},
				];
			}

			const wrap = columnWrap[header.key];

			if (value.excel) {
				return [
					{
						type: value.excel.type,
						value: value.excel.value ?? value.rawValue,
						format: value.excel.format,
						wrap,
					},
				] as Cell[];
			}

			return [
				{
					value: value.rawValue,
					wrap,
				},
			];
		});
	});

	const onClick = async () => {
		const period =
			props.timeMachine.interval === 'month'
				? moment(props.timeMachine.current[0]).format('MMMM YYYY')
				: props.timeMachine.interval === 'quarter'
				? `Q${moment(props.timeMachine.current[0]).format('Q YYYY')}`
				: props.timeMachine.interval === 'year'
				? moment(props.timeMachine.current[0]).format('YYYY')
				: props.timeMachine.current[0].toString();

		await writeXlsxFile([...headers, ...rows], {
			fileName: `${t(`analytics.metric.details.${props.detail.id}`)} - ${period}`,
			stickyRowsCount: hasFraction ? 2 : 1,
			columns: props.detail.headers.flatMap((header) => {
				if (header.type === 'fraction') {
					const widths = columnWidths['fraction'] as number[] | undefined;

					if (!widths) {
						return [{}, {}, {}];
					}

					return widths.map((width) => ({ width }));
				}

				const width = columnWidths[header.key] as number | undefined;

				return [{ width }];
			}),
		});
	};

	return (
		<Button
			type="transparent"
			small
			icon={<DownloadIcon />}
			title={t('analytics.metric.downloadButton')}
			onClick={onClick}
		/>
	);
}
